import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { db } from "../firebase";
import { styles } from "../styles";
import { notificationEvents } from "../utils/constants";
import { wantsAlert, isAdminTier, isManagerTier } from "../utils/capabilities";
import {
  enablePushNotifications,
  disablePushNotifications,
} from "../utils/pushNotifications";
import BackButton from "../components/BackButton";

const isNativeApp = () => Capacitor.getPlatform() !== "web";

/**
 * Per-user alert toggles. Each user controls which event types land in
 * their bell — admin/manager default to ALL on (uncheck to opt out),
 * technician/maintenance default to ALL off (check to opt in). Writes
 * `notificationPreferences` on the user doc; the bell client-side filters
 * notifications through wantsAlert() with these prefs.
 */
export default function NotificationSettingsPage({
  user,
  role,
  notificationPreferences = {},
  onBack,
}) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Browser push state — track the OS permission level so the UI can show
  // the right call to action (request / enabled / blocked).
  const [pushState, setPushState] = useState("unknown");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  useEffect(() => {
    // Native (Capacitor) uses the @capacitor/push-notifications plugin,
    // not the browser Notification API. Check permission via the plugin.
    if (isNativeApp()) {
      (async () => {
        try {
          const { PushNotifications } = await import(
            "@capacitor/push-notifications"
          );
          const perm = await PushNotifications.checkPermissions();
          // perm.receive: "prompt" | "prompt-with-rationale" | "denied" | "granted"
          if (perm.receive === "granted") setPushState("granted");
          else if (perm.receive === "denied") setPushState("denied");
          else setPushState("default");
        } catch {
          setPushState("default");
        }
      })();
      return;
    }
    // Web: browser Notification API
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPushState("unsupported");
      return;
    }
    setPushState(Notification.permission); // "default" | "granted" | "denied"
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    setPushMsg("");
    const res = await enablePushNotifications(user?.uid);
    if (res.ok) {
      setPushState("granted");
      setPushMsg("Push notifications enabled on this device.");
    } else if (res.reason === "no-vapid") {
      setPushMsg(
        "Missing VAPID key — add REACT_APP_FIREBASE_VAPID_KEY to your .env."
      );
    } else if (res.reason === "denied") {
      setPushState("denied");
      setPushMsg(
        isNativeApp()
          ? "Notifications blocked. Re-enable in Settings → Apps → TechnicianLogs → Notifications."
          : "Browser blocked notifications. Re-enable from your browser's site settings."
      );
    } else if (res.reason === "unsupported") {
      setPushState("unsupported");
      setPushMsg("Push isn't supported in this browser.");
    } else {
      setPushMsg(`Couldn't enable push: ${res.reason}`);
    }
    setPushBusy(false);
  };

  const disablePush = async () => {
    setPushBusy(true);
    setPushMsg("");
    const res = await disablePushNotifications(user?.uid);
    if (res.ok) {
      setPushMsg("Push disabled on this device.");
    } else {
      setPushMsg(`Couldn't disable: ${res.reason || "unknown"}`);
    }
    setPushBusy(false);
  };

  const toggle = async (key) => {
    const current = wantsAlert(role, notificationPreferences, key);
    const next = { ...notificationPreferences, [key]: !current };
    setSaving(true);
    setMsg("");
    try {
      await updateDoc(doc(db, `users/${user.uid}`), {
        notificationPreferences: next,
      });
    } catch (err) {
      setMsg(`Couldn't save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const defaultHint = isAdminTier(role)
    ? "Admins receive every alert by default — uncheck to silence one."
    : isManagerTier(role)
    ? "Managers receive every alert by default — uncheck to silence one."
    : "Technicians/maintenance receive no alerts by default — check the ones you want.";

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Notification Settings</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        Choose which alerts you want in your bell. {defaultHint}
      </p>
      {msg && <p style={{ color: "red", fontSize: "12px" }}>{msg}</p>}

      <div style={styles.card}>
        <strong>Push notifications</strong>
        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
          {isNativeApp()
            ? "Get OS-level push alerts on this device even when the app isn't open."
            : "Get the same alerts as desktop / OS notifications, even when this tab isn't focused."}
        </p>
        {pushState === "unsupported" && (
          <p style={{ fontSize: "12px", color: "#a33" }}>
            Push isn't supported in this browser (try Chrome, Edge, Firefox,
            or a recent Safari).
          </p>
        )}
        {pushState === "denied" && (
          <p style={{ fontSize: "12px", color: "#a33" }}>
            {isNativeApp()
              ? "Blocked. Re-enable in your device's Settings → Apps → TechnicianLogs → Notifications."
              : "Blocked by your browser. Re-enable in Site Settings → Notifications."}
          </p>
        )}
        {pushState === "granted" && (
          <>
            <p style={{ fontSize: "12px", color: "#1a7f37" }}>
              Enabled on this device.
            </p>
            <button
              style={styles.button}
              onClick={disablePush}
              disabled={pushBusy}
            >
              {pushBusy ? "Working..." : "Disable on this device"}
            </button>
          </>
        )}
        {(pushState === "default" || pushState === "unknown") && (
          <button
            style={styles.button}
            onClick={enablePush}
            disabled={pushBusy}
          >
            {pushBusy ? "Working..." : "Enable browser notifications"}
          </button>
        )}
        {pushMsg && (
          <p style={{ fontSize: "12px", marginTop: "6px" }}>{pushMsg}</p>
        )}
      </div>


      <div style={styles.card}>
        {notificationEvents
          .filter((ev) => !ev.availableTo || ev.availableTo.includes(role))
          .map((ev) => {
          const on = wantsAlert(role, notificationPreferences, ev.key);
          return (
            <label
              key={ev.key}
              style={{
                display: "block",
                padding: "8px 0",
                borderBottom: "1px solid #f0f0f0",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(ev.key)}
                disabled={saving}
              />{" "}
              <strong style={{ fontSize: "13px" }}>{ev.label}</strong>
              <div style={{ fontSize: "11px", color: "#666", marginLeft: "24px" }}>
                {ev.description}
              </div>
            </label>
          );
        })}
      </div>
    </>
  );
}
